import { useState, useEffect } from 'react';
import { ShieldCheck, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useAccountHygieneSettings, useUpdateAccountHygieneSettings, type AccountHygieneSettings } from '@/api/hooks/useSettings';
import { useAuthStore } from '@/store/auth.store';
import { RequireRole } from '@/components/auth/RequireRole';

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-accent-cyan' : 'bg-bg-surface-2'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-1'}`} />
    </button>
  );
}

function DaysSelect({ value, options, onChange, disabled }: {
  value: number;
  options: number[];
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
      className="h-9 rounded-md border border-border-subtle bg-bg-surface px-3 text-sm text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o} days</option>
      ))}
    </select>
  );
}

const INACTIVITY_OPTIONS = [30, 60, 90, 120, 180];
const INVITE_EXPIRY_OPTIONS = [30, 60, 90];

export function AccountHygienePage() {
  return (
    <RequireRole roles={['risk_manager', 'super_admin']} label="Security Posture">
      <AccountHygienePageBody />
    </RequireRole>
  );
}

function AccountHygienePageBody() {
  const { hasAnyRole } = useAuthStore();
  const canManageTenantSettings = hasAnyRole(['risk_manager', 'super_admin']);

  const { data: settings, isLoading } = useAccountHygieneSettings();
  const updateMutation = useUpdateAccountHygieneSettings();

  const [form, setForm] = useState<AccountHygieneSettings>({
    hygiene_scan_enabled: true,
    tenant_user_inactive_threshold_days: 90,
    pending_invitation_expiry_days: 60,
  });

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  function setField<K extends keyof AccountHygieneSettings>(key: K, val: AccountHygieneSettings[K]) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  async function handleSave() {
    try {
      await updateMutation.mutateAsync(form);
      toast.success('Account hygiene settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-text-primary flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-accent-cyan" />
          Security Posture
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Configure automated account hygiene policies. The nightly scanner enforces these thresholds.
        </p>
      </div>

      {/* Platform commitment notice */}
      <div className="flex items-start gap-2.5 rounded-lg border border-border-subtle bg-bg-surface px-4 py-3 text-xs text-text-secondary">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-text-tertiary" />
        <span>
          <strong className="text-text-primary">Platform commitment:</strong>{' '}
          Privileged role accounts (Super Admin, Risk Manager, Executive Viewer) are subject to a
          mandatory 90-day reverification requirement regardless of the inactivity threshold below.
          This is a platform-level security control and cannot be disabled.
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inactivity Suspension</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-text-primary">Enable account hygiene scanner</Label>
              <p className="text-xs text-text-secondary mt-0.5">
                When enabled, inactive team members are automatically suspended by the nightly scanner.
                SSO/federated accounts are managed by your identity provider and are excluded from
                local hygiene scanning and reverification monitoring.
              </p>
            </div>
            <Toggle
              checked={form.hygiene_scan_enabled}
              onChange={(v) => setField('hygiene_scan_enabled', v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-text-primary">Tenant user inactivity threshold</Label>
              <p className="text-xs text-text-secondary mt-0.5">
                Internal team members with no login activity for this many days will be auto-suspended. Range: 30–180 days.
              </p>
            </div>
            <DaysSelect
              value={form.tenant_user_inactive_threshold_days}
              options={INACTIVITY_OPTIONS}
              onChange={(v) => setField('tenant_user_inactive_threshold_days', v)}
              disabled={!form.hygiene_scan_enabled}
            />
          </div>

        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pending Invitation Expiry</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-text-primary">Invitation expiry period</Label>
              <p className="text-xs text-text-secondary mt-0.5">
                Active tenant relationship and assignment invitations not accepted within this period
                will be marked as lapsed. Note: expiry only lapses the pending invitation — existing
                active vendor accounts are not suspended. Range: 30–90 days.
              </p>
            </div>
            <DaysSelect
              value={form.pending_invitation_expiry_days}
              options={INVITE_EXPIRY_OPTIONS}
              onChange={(v) => setField('pending_invitation_expiry_days', v)}
              disabled={!form.hygiene_scan_enabled}
            />
          </div>
        </CardContent>
      </Card>

      {canManageTenantSettings && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      )}
      {!canManageTenantSettings && (
        <p className="text-xs text-text-tertiary text-right">
          Only Risk Managers and Super Admins can modify these settings.
        </p>
      )}
    </div>
  );
}
